import { memo, useCallback, useMemo } from 'react';
import { Fallback, HStack, VStack } from '@cbhq/cds-web/layout';
import { TextHeadline } from '@cbhq/cds-web/typography';
import { SegmentedTabs } from '@cbhq/cds-web/tabs/SegmentedTabs';
import { useAddLiquidityState } from '@/components/AddLiquidity/AddLiquidityProvider';

/**
 * Price range controls (title + SegmentedTabs) that render ABOVE the depth chart.
 * Contains the "Set price range" title, Full/Custom toggle, and token toggle.
 */
export const AddLiquidityPriceRangeControls = memo(function AddLiquidityPriceRangeControls({
  positionId,
}: {
  positionId: string | null;
}) {
  const {
    isFullRange,
    toggleIsFullRange,
    token0AsBaseToken,
    handleSetToken0AsBaseToken,
    handleSetToken1AsBaseToken,
    positionTokens,
  } = useAddLiquidityState();

  // Range toggle tabs (Full/Custom)
  const rangeTabs = useMemo(
    () => [
      { id: 'full', label: 'Full' },
      { id: 'custom', label: 'Custom' },
    ],
    [],
  );
  const activeRangeTab = useMemo(
    () => rangeTabs.find((tab) => tab.id === (isFullRange ? 'full' : 'custom')) ?? rangeTabs[0],
    [isFullRange, rangeTabs],
  );
  const handleRangeTabChange = useCallback(
    (activeTab: { id: string } | null) => {
      if (!activeTab) return;
      if ((activeTab.id === 'full' && !isFullRange) || (activeTab.id === 'custom' && isFullRange)) {
        toggleIsFullRange();
      }
    },
    [isFullRange, toggleIsFullRange],
  );

  // Token toggle tabs
  const tokenTabs = useMemo(
    () => [
      { id: 'token0', label: positionTokens[0].symbol },
      { id: 'token1', label: positionTokens[1].symbol },
    ],
    [positionTokens],
  );
  const activeTokenTab = useMemo(
    () =>
      tokenTabs.find((tab) => tab.id === (token0AsBaseToken ? 'token1' : 'token0')) ?? tokenTabs[0],
    [token0AsBaseToken, tokenTabs],
  );
  const handleTokenTabChange = useCallback(
    (activeTab: { id: string } | null) => {
      if (!activeTab) return;
      if (activeTab.id === 'token0') {
        handleSetToken1AsBaseToken();
      } else {
        handleSetToken0AsBaseToken();
      }
    },
    [handleSetToken0AsBaseToken, handleSetToken1AsBaseToken],
  );

  return (
    <VStack gap={1.5}>
      <TextHeadline as="p" testID="token-pair-price-range-title" noWrap>
        {positionId ? 'Price range' : 'Set price range'}
      </TextHeadline>

      <HStack justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
        {!positionId && (
          <SegmentedTabs
            tabs={rangeTabs}
            activeTab={activeRangeTab}
            onChange={handleRangeTabChange}
            testID="range-toggle"
          />
        )}

        <SegmentedTabs
          tabs={tokenTabs}
          activeTab={activeTokenTab}
          onChange={handleTokenTabChange}
          testID="token-toggle"
        />
      </HStack>
    </VStack>
  );
});

export const AddLiquidityPriceRangeControlsLoading = memo(
  function AddLiquidityPriceRangeControlsLoading({ positionId }: { positionId: string | null }) {
    return (
      <VStack gap={1.5}>
        <TextHeadline as="p" testID="token-pair-price-range-title" noWrap>
          {positionId ? 'Price range' : 'Set price range'}
        </TextHeadline>

        <HStack justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
          <Fallback width={160} height={40} shape="squircle" />
          <Fallback width={145} height={40} shape="squircle" />
        </HStack>
      </VStack>
    );
  },
);
